const SWITCHING_CLASS = 'theme-switching'

type TypedTransitionStarter = (options: {
  update: () => Promise<void>
  types: string[]
}) => ViewTransition

let running: ViewTransition | null = null
let generation = 0

const canAnimate = () =>
  typeof document !== 'undefined' &&
  typeof document.startViewTransition === 'function' &&
  typeof window.matchMedia === 'function' &&
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches

// A `theme` type scopes the crossfade in globals.css so it can't hijack any
// other view transition. Engines without types take the callback form and
// simply get the default root crossfade.
const startTyped = (update: () => Promise<void>): ViewTransition => {
  if (typeof CSS !== 'undefined' && CSS.supports('selector(:active-view-transition-type(theme))')) {
    return (document.startViewTransition as unknown as TypedTransitionStarter)({
      update,
      types: ['theme'],
    })
  }
  return document.startViewTransition(update)
}

/**
 * Runs `apply` (the class swap and anything that must land in the same
 * snapshot) inside one crossfade of the whole screen. Instant when the member
 * prefers reduced motion or the browser has no View Transitions.
 *
 * `apply` runs synchronously when it can't animate, so callers and tests see
 * the same ordering as a plain swap.
 */
export function runThemeTransition(apply: () => void): void {
  if (!canAnimate()) {
    apply()
    return
  }

  // A second toggle mid-fade jumps the first to its end; its `finished`
  // handler must not clear the class the new transition just set.
  running?.skipTransition()
  const mine = ++generation
  const root = document.documentElement
  root.classList.add(SWITCHING_CLASS)

  const transition = startTyped(async () => {
    apply()
    // Charts re-theme in a React effect; one task lets it flush so the new
    // snapshot already holds the new chart colours.
    await new Promise<void>((resolve) => setTimeout(resolve, 0))
  })
  running = transition

  void transition.finished.finally(() => {
    if (mine !== generation) return
    root.classList.remove(SWITCHING_CLASS)
    running = null
  })
}
